import { useState, useRef, useEffect } from "react";
import moment from "moment";
import { getTranslations } from "services/api/translations/redux";
import { useSelector } from "react-redux";
import { AxiosResponse } from "axios";
import { isProd } from "./environment";



export function classNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}

export const transformDateToMomentDate = (date: string | null): Date | null => {
  return date ? moment(date).toDate() : null;
};

export const transformDateToLocalDateString = (
  date: Date | null
): string | null => {
  return date ? moment(date).format("DD.MM.YYYY.") : null;
};

/** Datum i vrijeme u lokalnom zapisu, npr. "02.10.2026. 08:15". */
export const transformDateToLocalDateTimeString = (
  date: Date | null
): string | null => {
  return date ? moment(date).format("DD.MM.YYYY. HH:mm") : null;
};

/**
 * Dodaje mjesece datumu. Ide preko momenta jer on ispravno rješava kraj mjeseca —
 * `new Date(y, m + 1, 31)` bi 31.01. prelio u 03.03. umjesto u 28./29.02.
 */
export const addMonths = (date: Date, months: number): Date =>
  moment(date).add(months, "months").toDate();

/** Puni mjeseci između dva datuma (zaokruženo prema dolje). */
export const diffInMonths = (from: Date, to: Date): number =>
  moment(to).diff(moment(from), "months");

/** Razdoblje u obliku „01.08.2026. – 31.07.2027."; bez kraja se ispisuje „od 01.08.2026.". */
export const formatPeriod = (from: Date | null, to: Date | null): string => {
  if (!from && !to) return "";
  if (!to) return `od ${transformDateToLocalDateString(from)}`;
  if (!from) return `do ${transformDateToLocalDateString(to)}`;

  return `${transformDateToLocalDateString(from)} – ${transformDateToLocalDateString(to)}`;
};

export const transformRoute = (pathRegex: string, params: any) => {
  let segments = pathRegex.split("/");

  const result = segments
    .map((segment) => {
      let offset = segment.indexOf(":?") + 1;

      if (offset) {
        let key = segment.slice(offset + 1);
        return params[key];
      }

      offset = segment.indexOf(":") + 1;
      if (!offset) return segment;

      let key = segment.slice(offset);

      return params[key];
    })
    .join("/")
    // Remove trailing "/"
    .replace(/\/+$/, "")
    // Remove redundant "/"
    .replace(/\/{2,}/, "/");

  return result;
};

export const isActiveTab = (route: string, pathname: string) => {
  let isActive = false;

  if (pathname.slice(-1) !== "/") pathname = pathname + "/";

  isActive = route === pathname.substring(0, pathname.lastIndexOf("/"));

  return isActive;
};

export const useObserveElementWidth = <T extends HTMLElement>() => {
  const [width, setWidth] = useState(0);
  const ref = useRef<T>(null);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      setWidth(entries[0].contentRect.width);
    });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      ref.current && observer.unobserve(ref.current);
    };
  }, []);

  return {
    width,
    ref,
  };
};

export type CardSize = "sm" | "md" | "lg";

/**
 * Veličina kartice po vlastitoj širini, ne po širini prozora — widget na nadzornoj
 * ploči može biti uzak i na velikom ekranu. Vraća se uz `ref` koji se stavlja na
 * omotač, a `size` ide u `data-size` pa se stilovi pišu Tailwind varijantama
 * (`data-[size=sm]:text-xl`).
 */
export const useCardSize = <T extends HTMLElement>() => {
  const { width, ref } = useObserveElementWidth<T>();

  const size: CardSize = width === 0 || width >= 520 ? "lg" : width >= 320 ? "md" : "sm";

  return { size, width, ref };
};

export const formatCurrency = new Intl.NumberFormat("hr-HR", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Formatira veličinu iz kilobajta u čitljiv oblik (KB / MB / GB). */
export const formatFileSize = (kb: number): string => {
  if (!kb || kb <= 0) return "0 KB";
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(gb < 10 ? 2 : 1)} GB`;
};

export const formatDecimal = (value: string) => {
  // Uzmi trenutnu vrijednost iz inputa i ukloni sve osim brojeva i točke
  let newValue = value.replace(/[^\d.]/g, "");

  // Pretvori string u broj i formatiraj ga
  let number = parseFloat(value);
  if (!isNaN(number)) {
    // Postavi maksimalno dva decimalna mjesta
    number = Math.floor(number * 100) / 100;

    // Formatiraj broj sa zarezima kao razdelnicima tisućica
    newValue = number.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } else {
    newValue = "0.00";
  }

  return newValue;
};

export const downloadFile = (
  response: AxiosResponse,
  fileExtension: string,
  fileName?: string
) => {
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  const currentDatetime = moment().format("YYYY-MM-DD_h:mm:ss");
  const downloadFileName = fileName || `${currentDatetime}.${fileExtension}`;
  link.href = url;
  link.setAttribute("download", downloadFileName);
  document.body.appendChild(link);
  link.click();
};

/**
 * Preuzimanje sadržaja koji je već u memoriji (npr. XML generiran na zahtjev). Za odgovore
 * s API-ja koristi `downloadFile` — ovaj radi s gotovim tekstom, bez AxiosResponse.
 */
export const downloadTextFile = (
  content: string,
  fileName: string,
  mimeType = "text/plain"
) => {
  const url = window.URL.createObjectURL(new Blob([content], { type: mimeType }));
  const link = document.createElement("a");

  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const isValidDate = (date: string): boolean => {
  const [day, month, year] = date.split(".").map(Number);

  // Provjera osnovnih vrijednosti
  if (!day || !month || !year) return false;

  // Kreiramo datum
  const parsedDate = new Date(year, month - 1, day);

  // Provjera je li dan, mjesec i godina validni
  return (
    parsedDate.getDate() === day &&
    parsedDate.getMonth() === month - 1 &&
    parsedDate.getFullYear() === year
  );
};

// Custom hook za prevođenje
export const useTranslate = () => {
  const translations = useSelector(getTranslations);

  // Funkcija za dohvaćanje prijevoda
  const translate = (key: string): string => {
    return (
      translations.items?.find((item) => item.key.trim() === key.trim())
        ?.value || `${isProd() ? "" : key}`
    ); // Ako prijevod ne postoji, vraća originalni ključ
  };

  return { t: translate };
};

export const setLanguageCurrentLocal = (languageId: string) => {
  localStorage.setItem("languageId", languageId);
};

export const getLanguageCurrentLocal = () => {
  return localStorage.getItem("languageId");
};

export const getContentTypeFromUrl = (url: string): string => {
  const extension = url.split('.').pop()?.toLowerCase().split('?')[0] || '';
  const mimeTypes: Record<string, string> = {
    'png': 'image/png',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'gif': 'image/gif',
    'webp': 'image/webp',
    'svg': 'image/svg+xml',
    'bmp': 'image/bmp',
    'ico': 'image/x-icon',
  };
  return mimeTypes[extension] || 'image/png';
};

export const getFileNameFromUrl = (url: string): string => {
  const urlPath = url.split('?')[0];
  return urlPath.split('/').pop() || 'logo';
};

export const stripHtml = (html: string): string =>
  html.replace(/<[^>]*>/g, "").trim();

/**
 * Parsira datumski query param u lokalni Date.
 * Podržava čist "YYYY-MM-DD" (endOfDay=false -> 00:00, endOfDay=true -> 23:59:59.999) i puni ISO string.
 */
export const parseDateParam = (
  value: string | null | undefined,
  endOfDay: boolean = false
): Date | null => {
  if (!value) return null;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    const [, y, m, d] = dateOnly;
    return endOfDay
      ? new Date(+y, +m - 1, +d, 23, 59, 59, 999)
      : new Date(+y, +m - 1, +d, 0, 0, 0, 0);
  }
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Inverz od `parseDateParam` — formatira Date u lokalni "YYYY-MM-DD" za query param (ili null).
 */
export const formatDateParam = (date: Date | null | undefined): string | null => {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

